import { ref } from 'vue'
import { defineStore } from 'pinia'
import api from '../api/client'
import { BUNNY_FONTS, bunnyFontsUrl, fontFamilyCss } from '../utils/bunnyFonts'

/**
 * Sitebrede thema-instellingen: logo plus look & feel (lettertypes,
 * kopstijlen, kleuren). Lezen is publiek — bezoekers zien het thema ook.
 *
 * De stijlen worden toegepast door één <style id="theme-style"> in <head>
 * te vullen (alleen regels voor daadwerkelijk ingestelde waarden, dus
 * niets ingesteld = exact de standaardvormgeving) en de benodigde fonts
 * via één <link id="theme-fonts"> van Bunny.net te laden.
 */

export type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4'

export interface HeadingStyle {
  font: string | null
  // `size`/`lineHeight` gelden voor desktop, `mobileSize`/`mobileLineHeight`
  // onder de sm-breakpoint. De sleutel `size` is bewust niet omgedoopt naar
  // `desktopSize`: bestaande thema's hebben hem al opgeslagen.
  size: number | null
  lineHeight: number | null
  mobileSize: number | null
  mobileLineHeight: number | null
  color: string | null
}

/** Waarvoor de invoervelden in het koppenpaneel gelden. */
export type Breakpoint = 'desktop' | 'mobile'

export interface FooterStyles {
  /** Footer tonen? Standaard ja; op false verdwijnt de hele footer. */
  enabled: boolean
  /** Rich text (HTML). Leeg = geen eigen footerinhoud. */
  content: string
  backgroundColor: string | null
  /**
   * Eigen tekstkleur. Leeg = automatisch: dan bepaalt de helderheid van de
   * achtergrond of de tekst wit of bijna-zwart wordt (readableTextColor).
   */
  textColor: string | null
}

export type CornerTarget = 'sections' | 'columns' | 'cards' | 'images' | 'rows'

export interface CornerStyle {
  // Afgeronde hoeken aan/uit. Standaard aan (de huidige vormgeving);
  // op false worden de hoeken vierkant. `radius` is de afronding in px
  // wanneer aan (standaard 8, gelijk aan de bestaande rounded-lg).
  rounded: boolean
  radius: number
}

/**
 * De kop in een hero-sectie.
 *
 * Die kop is een h1 en volgt dus standaard de h1-instellingen. Maar een hero
 * is een band over de volle breedte met een eigen achtergrond; daar wil je de
 * kop vaak groter en strakker dan een gewone paginatitel. Leeg gelaten = de
 * h1-waarde, zodat wie hier niets instelt precies houdt wat hij had.
 *
 * Lettertype en kleur staan er bewust niet bij: het lettertype volgt de h1
 * (één koplettertype op de site), en de kleur kiest de hero per sectie zelf
 * tussen licht en donker, afgestemd op zijn eigen achtergrond.
 */
export interface HeroTitleStyle {
  size: number | null
  lineHeight: number | null
  mobileSize: number | null
  mobileLineHeight: number | null
}

export interface ThemeStyles {
  baseFont: string | null
  backgroundColor: string | null
  borderColor: string | null
  buttonColor: string | null
  corners: Record<CornerTarget, CornerStyle>
  headings: Record<HeadingLevel, HeadingStyle>
  heroTitle: HeroTitleStyle
  footer: FooterStyles
}

export const HEADING_LEVELS: HeadingLevel[] = ['h1', 'h2', 'h3', 'h4']
export const CORNER_TARGETS: CornerTarget[] = ['sections', 'columns', 'cards', 'rows', 'images']
export const DEFAULT_RADIUS = 8
export const MAX_RADIUS = 48
export const MIN_LINE_HEIGHT = 0.8
export const MAX_LINE_HEIGHT = 3
/** Onder deze breedte gelden de mobiele koptekstwaarden (Tailwind sm = 640px). */
export const MOBILE_MAX_WIDTH = '639.98px'

export function emptyStyles(): ThemeStyles {
  const heading = (): HeadingStyle => ({
    font: null,
    size: null,
    lineHeight: null,
    mobileSize: null,
    mobileLineHeight: null,
    color: null,
  })
  const corner = (): CornerStyle => ({ rounded: true, radius: DEFAULT_RADIUS })
  return {
    baseFont: null,
    backgroundColor: null,
    borderColor: null,
    buttonColor: null,
    corners: { sections: corner(), columns: corner(), cards: corner(), rows: corner(), images: corner() },
    headings: { h1: heading(), h2: heading(), h3: heading(), h4: heading() },
    heroTitle: { size: null, lineHeight: null, mobileSize: null, mobileLineHeight: null },
    footer: { enabled: true, content: '', backgroundColor: null, textColor: null },
  }
}

// Zelfde grenzen als de backend-validatie; hier nogmaals als guard zodat
// er nooit ongecontroleerde tekst in de gegenereerde CSS belandt.
const HEX_RE = /^#[0-9a-fA-F]{6}$/
const KNOWN_FONTS = new Set(BUNNY_FONTS.map((f) => f.name))

function safeColor(value: unknown): string | null {
  return typeof value === 'string' && HEX_RE.test(value) ? value : null
}

function safeFont(value: unknown): string | null {
  return typeof value === 'string' && KNOWN_FONTS.has(value) ? value : null
}

function safeSize(value: unknown): number | null {
  const n = Number(value)
  return Number.isInteger(n) && n >= 10 && n <= 96 ? n : null
}

/** Boolean met standaard true: ontbrekende/onbekende waarde = aan (rond). */
function safeBool(value: unknown): boolean {
  return value === false ? false : true
}

/** Afronding in px, binnen [0, MAX_RADIUS]; anders de standaard. */
function safeRadius(value: unknown): number {
  const n = Number(value)
  return Number.isInteger(n) && n >= 0 && n <= MAX_RADIUS ? n : DEFAULT_RADIUS
}

/**
 * Regelafstand als eenheidsloze factor (1.2 = 120%). Eenheidsloos is de
 * typografische norm: de afstand schaalt dan mee met de lettergrootte.
 * Eén decimaal, binnen de grenzen van de backend-validatie.
 */
function safeLineHeight(value: unknown): number | null {
  const n = Number(value)
  if (!Number.isFinite(n) || n < MIN_LINE_HEIGHT || n > MAX_LINE_HEIGHT) return null
  return Math.round(n * 10) / 10
}

/** Kleur donkerder maken (voor de hover-stand van knoppen). */
function shade(hex: string, factor: number): string {
  const channel = (i: number) =>
    Math.round(parseInt(hex.slice(i, i + 2), 16) * factor)
      .toString(16)
      .padStart(2, '0')
  return `#${channel(1)}${channel(3)}${channel(5)}`
}

/** Witte of donkere tekst, afhankelijk van de helderheid van de achtergrond. */
export function readableTextColor(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  const brightness = (r * 299 + g * 587 + b * 114) / 1000
  return brightness > 160 ? '#111827' : '#ffffff'
}

/** Relatieve luminantie volgens WCAG 2.x (sRGB, gamma-gecorrigeerd). */
function relativeLuminance(hex: string): number {
  const channel = (i: number) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5)
}

/**
 * Contrastverhouding tussen twee kleuren (1 – 21) volgens WCAG. Let op: dit
 * is een andere formule dan readableTextColor hierboven — die kiest snel een
 * bruikbare standaard, dit meet of een combinatie écht voldoet (AA-drempel
 * voor normale tekst is 4,5:1).
 */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  const [light, dark] = la > lb ? [la, lb] : [lb, la]
  return (light + 0.05) / (dark + 0.05)
}

/** WCAG AA voor normale tekst. */
export const AA_CONTRAST = 4.5

// De API-client camelizet alle response-sleutels (base_font → baseFont).
function fromApiStyles(raw: any): ThemeStyles {
  const styles = emptyStyles()
  if (!raw || typeof raw !== 'object') return styles
  styles.baseFont = safeFont(raw.baseFont)
  styles.backgroundColor = safeColor(raw.backgroundColor)
  styles.borderColor = safeColor(raw.borderColor)
  styles.buttonColor = safeColor(raw.buttonColor)
  for (const target of CORNER_TARGETS) {
    const corner = raw.corners?.[target] ?? {}
    styles.corners[target] = {
      rounded: safeBool(corner.rounded),
      radius: safeRadius(corner.radius),
    }
  }
  for (const level of HEADING_LEVELS) {
    const heading = raw.headings?.[level] ?? {}
    styles.headings[level] = {
      font: safeFont(heading.font),
      size: safeSize(heading.size),
      lineHeight: safeLineHeight(heading.lineHeight),
      mobileSize: safeSize(heading.mobileSize),
      mobileLineHeight: safeLineHeight(heading.mobileLineHeight),
      color: safeColor(heading.color),
    }
  }
  const hero = raw.heroTitle ?? {}
  styles.heroTitle = {
    size: safeSize(hero.size),
    lineHeight: safeLineHeight(hero.lineHeight),
    mobileSize: safeSize(hero.mobileSize),
    mobileLineHeight: safeLineHeight(hero.mobileLineHeight),
  }
  // Footer-HTML is al door de backend gesaneerd; bij het renderen gaat het
  // daarnaast nog door DOMPurify (zie AppFooter).
  styles.footer = {
    enabled: safeBool(raw.footer?.enabled),
    content: typeof raw.footer?.content === 'string' ? raw.footer.content : '',
    backgroundColor: safeColor(raw.footer?.backgroundColor),
    textColor: safeColor(raw.footer?.textColor),
  }
  return styles
}

function toApiStyles(styles: ThemeStyles): Record<string, any> {
  return {
    base_font: styles.baseFont,
    background_color: styles.backgroundColor,
    border_color: styles.borderColor,
    button_color: styles.buttonColor,
    corners: Object.fromEntries(
      CORNER_TARGETS.map((target) => [target, { ...styles.corners[target] }])
    ),
    // Expliciet snake_case, net als de overige sleutels hierboven: de backend
    // valideert/bewaart snake_case en de API-client camelizet het antwoord weer.
    headings: Object.fromEntries(
      HEADING_LEVELS.map((level) => {
        const h = styles.headings[level]
        return [level, {
          font: h.font,
          size: h.size,
          line_height: h.lineHeight,
          mobile_size: h.mobileSize,
          mobile_line_height: h.mobileLineHeight,
          color: h.color,
        }]
      })
    ),
    hero_title: {
      size: styles.heroTitle.size,
      line_height: styles.heroTitle.lineHeight,
      mobile_size: styles.heroTitle.mobileSize,
      mobile_line_height: styles.heroTitle.mobileLineHeight,
    },
    footer: {
      enabled: styles.footer.enabled,
      content: styles.footer.content,
      background_color: styles.footer.backgroundColor,
      text_color: styles.footer.textColor,
    },
  }
}

/** Eén <head>-element aanmaken/bijwerken, of verwijderen als er niets te tonen is. */
function upsertHeadElement(tag: 'style' | 'link', id: string, apply: ((el: HTMLElement) => void) | null) {
  let el = document.getElementById(id)
  if (!apply) {
    el?.remove()
    return
  }
  if (!el) {
    el = document.createElement(tag)
    el.id = id
    document.head.appendChild(el)
  }
  apply(el)
}

/**
 * Genereer de CSS voor alle ingestelde themawaarden.
 *
 * Geëxporteerd omdat het een zuivere functie is: de themaregels zijn met
 * losse invoer te controleren, zonder browser of gemonteerde app.
 */
export function buildThemeCss(s: ThemeStyles): string {
  const rules: string[] = []

  if (s.baseFont) {
    rules.push(`html { font-family: ${fontFamilyCss(s.baseFont)}; }`)
  }

  if (s.buttonColor) {
    const text = readableTextColor(s.buttonColor)
    rules.push(
      `.btn-primary { background-color: ${s.buttonColor}; color: ${text}; }`,
      `.btn-primary:hover { background-color: ${shade(s.buttonColor, 0.85)}; color: ${text}; }`
    )
  }

  if (s.backgroundColor) {
    // Dubbele class om zeker te winnen van de bg-gray-50-utility, plus de
    // p-6-goot van <main> eromheen meekleuren op documentatiepagina's.
    rules.push(
      `.themed-page.themed-page { background-color: ${s.backgroundColor}; }`,
      `#main-content:has(.themed-page) { background-color: ${s.backgroundColor}; }`
    )
  }

  if (s.borderColor) {
    rules.push(
      `.themed-page .themed-section, .themed-page .themed-column, .themed-page .card-block { border-color: ${s.borderColor}; }`
    )
  }

  // Afgeronde hoeken van secties, kolommen en kaarten. We overschrijven de
  // standaard rounded-lg op de wrapper zelf (niet op inhoud erin) alleen
  // wanneer de instelling afwijkt van de standaard (rond, 8px).
  const cornerSelectors: Record<CornerTarget, string> = {
    sections: '.themed-page .themed-section',
    columns: '.themed-page .themed-column',
    cards: '.themed-page .card-block',
    rows: '.themed-page .themed-row',
    // Ook afbeeldingen in rich text: die hadden geen eigen ronding en bleven
    // daardoor vierkant terwijl het thema op rond stond.
    images:
      '.themed-page .themed-image img, .themed-page .themed-image > a, .themed-page .prose img',
  }
  for (const target of CORNER_TARGETS) {
    const c = s.corners[target]
    if (c.rounded && c.radius === DEFAULT_RADIUS) continue
    const radius = c.rounded ? c.radius : 0
    rules.push(`${cornerSelectors[target]} { border-radius: ${radius}px; }`)
  }

  /*
   * Een eigen hoekafronding per afbeelding staat als inline stijl op de
   * afbeelding zelf (zie EditorImage / het afbeeldingsblok). Die wint vanzelf
   * van de regels hierboven, dus daar is hier geen CSS voor nodig: wat je hier
   * schrijft is de standaard, en de afbeelding mag ervan afwijken.
   */

  // Kopstijlen gelden voor rich-text-inhoud en de vaste paginakoppen.
  const headingSelectors: Record<HeadingLevel, string> = {
    h1: '.themed-page .prose h1, .themed-page .page-title',
    h2: '.themed-page .prose h2, .themed-page .section-title',
    h3: '.themed-page .prose h3',
    h4: '.themed-page .prose h4',
  }

  /*
   * De hero-titel is een h1 en volgt dus standaard de h1-instellingen —
   * anders staat die instelling er wel, maar doet ze niets op de meest
   * zichtbare kop van de pagina.
   *
   * Maat en regelhoogte kunnen daar sinds kort van afwijken (heroTitle): een
   * hero is een band over de volle breedte, waar je die kop vaak groter en
   * strakker wilt dan een gewone paginatitel. Niet ingevuld = de h1-waarde,
   * dus wie hier niets instelt houdt precies wat hij had.
   *
   * Het lettertype volgt altijd de h1 (één koplettertype op de site). De
   * kleur blijft er bewust buiten: een hero kiest per sectie zelf tussen
   * lichte en donkere tekst, afgestemd op zijn achtergrond. Zou de themakleur
   * daar overheen gaan, dan wordt donkere tekst op een donkere hero
   * onleesbaar zonder dat iemand die instelling heeft aangeraakt.
   */
  const heroTitleSelector = '.themed-page .hero-title'
  // Mobiele afwijkingen komen ná de desktopregels in één media query, zodat
  // ze binnen dezelfde specificiteit winnen onder de sm-breakpoint.
  const mobileRules: string[] = []
  for (const level of HEADING_LEVELS) {
    const h = s.headings[level]
    const props: string[] = []
    if (h.font) props.push(`font-family: ${fontFamilyCss(h.font)}`)
    if (h.size) props.push(`font-size: ${h.size}px`)
    if (h.lineHeight) props.push(`line-height: ${h.lineHeight}`)
    if (h.color) props.push(`color: ${h.color}`)
    if (props.length) rules.push(`${headingSelectors[level]} { ${props.join('; ')}; }`)

    const mobileProps: string[] = []
    if (h.mobileSize) mobileProps.push(`font-size: ${h.mobileSize}px`)
    if (h.mobileLineHeight) mobileProps.push(`line-height: ${h.mobileLineHeight}`)
    if (mobileProps.length) {
      mobileRules.push(`  ${headingSelectors[level]} { ${mobileProps.join('; ')}; }`)
    }
  }

  // Hero-titel: eigen maat/regelhoogte waar ingesteld, anders die van h1.
  const h1 = s.headings.h1
  const hero = s.heroTitle
  const heroProps: string[] = []
  if (h1.font) heroProps.push(`font-family: ${fontFamilyCss(h1.font)}`)
  const heroSize = hero.size ?? h1.size
  const heroLineHeight = hero.lineHeight ?? h1.lineHeight
  if (heroSize) heroProps.push(`font-size: ${heroSize}px`)
  if (heroLineHeight) heroProps.push(`line-height: ${heroLineHeight}`)
  if (heroProps.length) rules.push(`${heroTitleSelector} { ${heroProps.join('; ')}; }`)

  const heroMobileProps: string[] = []
  const heroMobileSize = hero.mobileSize ?? h1.mobileSize
  const heroMobileLineHeight = hero.mobileLineHeight ?? h1.mobileLineHeight
  if (heroMobileSize) heroMobileProps.push(`font-size: ${heroMobileSize}px`)
  if (heroMobileLineHeight) heroMobileProps.push(`line-height: ${heroMobileLineHeight}`)
  if (heroMobileProps.length) {
    mobileRules.push(`  ${heroTitleSelector} { ${heroMobileProps.join('; ')}; }`)
  }
  if (mobileRules.length) {
    rules.push(`@media (max-width: ${MOBILE_MAX_WIDTH}) {\n${mobileRules.join('\n')}\n}`)
  }

  return rules.join('\n')
}

/** Unieke, geldige fontnamen die het thema gebruikt. */
export function fontsInUse(s: ThemeStyles): string[] {
  const fonts = new Set<string>()
  if (s.baseFont) fonts.add(s.baseFont)
  for (const level of HEADING_LEVELS) {
    const font = s.headings[level].font
    if (font) fonts.add(font)
  }
  return [...fonts]
}

function applyStylesToDocument(s: ThemeStyles) {
  const fonts = fontsInUse(s)
  upsertHeadElement('link', 'theme-fonts', fonts.length
    ? (el) => {
        el.setAttribute('rel', 'stylesheet')
        el.setAttribute('href', bunnyFontsUrl(fonts))
      }
    : null)

  const css = buildThemeCss(s)
  upsertHeadElement('style', 'theme-style', css ? (el) => { el.textContent = css } : null)
}

// Laatst bekende thema per tenant-host, zodat het thema (kleuren, fonts,
// logo) bij een volgende paginalading meteen — synchroon, vóór het eerste
// netwerkantwoord — wordt toegepast. Zonder deze cache verschijnt elke
// full page load eerst even in de standaardvormgeving tot /settings/theme
// terug is (flits van kleuren/fonts). Het netwerkantwoord blijft leidend
// en ververst de cache (stale-while-revalidate).
const THEME_CACHE_KEY = `pentrical_theme:${window.location.host}`

function readThemeCache(): any | null {
  try {
    return JSON.parse(localStorage.getItem(THEME_CACHE_KEY) ?? 'null')
  } catch {
    return null
  }
}

function writeThemeCache(data: any) {
  try {
    localStorage.setItem(THEME_CACHE_KEY, JSON.stringify(data))
  } catch {
    // Volle/geblokkeerde storage: geen cache, verder geen gevolgen.
  }
}

export const useThemeStore = defineStore('theme', () => {
  const logoType = ref<'text' | 'image'>('text')
  const logoUrl = ref<string | null>(null)
  const logoText = ref('Design System')
  // Nog niets ingesteld (nieuwe oplevering): header toont dan het
  // Pentrical-logo i.p.v. de tekst-fallback.
  const isDefault = ref(true)
  const isSaving = ref(false)
  const styles = ref<ThemeStyles>(emptyStyles())

  function apply(data: any) {
    logoType.value = data.logoType === 'image' ? 'image' : 'text'
    logoUrl.value = data.logoUrl ?? null
    logoText.value = data.logoText || 'Design System'
    isDefault.value = Boolean(data.isDefault)
    styles.value = fromApiStyles(data.styles)
    applyStylesToDocument(styles.value)
  }

  function applyAndCache(data: any) {
    apply(data)
    writeThemeCache(data)
  }

  // Direct bij het aanmaken van de store (app-boot): cache toepassen.
  // De waarden gaan door dezelfde guards als een API-antwoord.
  const cached = readThemeCache()
  if (cached) apply(cached)

  async function fetchTheme() {
    try {
      const response = await api.get('/settings/theme')
      applyAndCache(response.data)
    } catch {
      // Stil falen: de header valt terug op de tekstvariant.
    }
  }

  async function updateTheme(data: { logoType?: 'text' | 'image'; logoText?: string; styles?: ThemeStyles }) {
    isSaving.value = true
    try {
      const payload: Record<string, any> = {}
      if (data.logoType !== undefined) payload.logo_type = data.logoType
      if (data.logoText !== undefined) payload.logo_text = data.logoText
      if (data.styles !== undefined) payload.styles = toApiStyles(data.styles)
      const response = await api.put('/settings/theme', payload)
      applyAndCache(response.data)
    } finally {
      isSaving.value = false
    }
  }

  async function uploadLogo(file: File) {
    isSaving.value = true
    try {
      const formData = new FormData()
      formData.append('logo', file)
      const response = await api.post('/settings/theme/logo', formData)
      applyAndCache(response.data)
    } finally {
      isSaving.value = false
    }
  }

  /** Het logo weghalen. De server zet het type terug op tekst. */
  async function deleteLogo() {
    isSaving.value = true
    try {
      const response = await api.delete('/settings/theme/logo')
      applyAndCache(response.data)
    } finally {
      isSaving.value = false
    }
  }

  return { logoType, logoUrl, logoText, isDefault, isSaving, styles, fetchTheme, updateTheme, uploadLogo, deleteLogo }
})
